
import { Observable } from 'rxjs';
import { Entity } from '../store/specialized/entity/types/entity';
import { Dictionary } from '../types/dictionary';
import { Sort } from '../sort/types/sort';

export type Collection<T extends Entity> = T[];
export type Id = string | number | object;

export type Result<T> = { result: T };
export type PaginationParams = { limit?: number, offset?: number };
export type SortParams = {sort?: string};

export interface LoadObjectParameters {
  pathParams?: Dictionary<unknown>;
  queryParams?: Dictionary<unknown>;
  body?: Record<string, unknown>
}

export interface LoadCollectionItemParameters {
  pathParams?: Dictionary<unknown>;
}

export interface LoadDictionaryParameters {
  pathParams?: Dictionary<unknown>;
}

export interface SaveDictionaryParameters {
  pathParams?: Dictionary<unknown>;
}

export interface LoadListParameters {
  pathParams?: Dictionary<unknown>;
  query?: Dictionary<unknown>;
  sort?: Sort<any>[];
  limit?: number;
  offset?: number;
}

export interface SaveListParameters {
  pathParams?: Dictionary<unknown>;
  body?: Record<string, unknown>
}

export interface DeleteListParameters<T> {
  item: T
  pathParams?: Dictionary<unknown>;
}

export interface PatchListParameters<T> {
  patch: T
  pathParams?: Dictionary<unknown>;
}

export interface LoadCollectionParameters {
  pathParams?: Dictionary<unknown>;
  query?: Dictionary<unknown>;
  sort?: Sort<any>[];
  limit?: number;
  offset?: number;
}
export interface QueryCollectionParameters {
  pathParams?: Dictionary<unknown>;
  queryParams?: Dictionary<unknown>;
  query?: Dictionary<unknown>;
  sort?: Sort<any>[];
  limit?: number;
  offset?: number;
}

export interface CRUDParameters {
  pathParams?: Dictionary<unknown>;
}

export interface CRUDCollectionParameters {
  pathParams?: Dictionary<unknown>;
}

export interface ReadonlyRepository<TResult, TParams> {
  load(params: TParams): Promise<TResult>;
}

export interface Repository<T, TResult, TLoadParams, TCrudParams> extends ReadonlyRepository<TResult, TLoadParams>{
  create(value: T, params: TCrudParams): Promise<TResult>;
  update(value: T, params: TCrudParams): Promise<TResult>;
  patch(patch: Partial<T>, params: TCrudParams): Promise<TResult>;
  delete(params: TCrudParams): Promise<TResult>;
}

export interface ReadonlyDetailRepository<TParent, TResult, TParams> {
  load(parent: TParent, params: TParams): Promise<TResult>;
}

export interface ReadonlyListRepository<TManyResult, TParams> {
  load(params: TParams): Promise<TManyResult>;
}

export interface ListRepository<TItem, TManyResult, TLoadParams, TCrudParams> extends ReadonlyListRepository<TManyResult, TLoadParams> {
  create(value: TItem[], params: TCrudParams): Promise<TManyResult>;
  update(value: TItem[], params?: TCrudParams): Promise<TManyResult>;
  delete(params?: TCrudParams): Promise<TManyResult>;
}

export interface ReadonlyCollectionRepository<TResult, TLoadParams> {
  load(params: TLoadParams): Promise<TResult>;
}

export interface CollectionRepository<TItem, TResult, TCrudResult, TLoadParams, TCrudParams> extends ReadonlyCollectionRepository<TResult, TLoadParams>{
  create(value: TItem, params: TCrudParams): Promise<TCrudResult>;
  update(id: Id, value: TItem, params: TCrudParams): Promise<TCrudResult>;
  patch(id: Id, changes: Partial<TItem>, params: TCrudParams): Promise<TCrudResult>;
  delete(id: Id, params: TCrudParams): Promise<TCrudResult>;
}

export interface ReadonlyDictionaryRepository<TResult, TLoadParams> {
  load(params: TLoadParams): Promise<TResult>;
}

export interface DictionaryRepository<T, TResult, TCrudResult, TLoadParams, TCrudParams> extends ReadonlyDictionaryRepository<TResult, TLoadParams> {
  save(dictionary: Dictionary<T>, params: TCrudParams): Promise<TResult>;
  saveItem(key: string, value: T, params: TCrudParams): Promise<TCrudResult>;
  deleteItem(key: string, params: TCrudParams): Promise<TCrudResult>;
  delete(params: TCrudParams): Promise<TResult>;
}
